import {Chess} from 'chess.js';

export const DEFAULT_FEN =
  'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

export interface VerifyResponse {
  valid?: number;
  incorrect?: number;
  stop?: boolean;
  opponent_move?: string;
  move_count?: number;
  message?: string;
  /** Engine's best move (SAN); ActivePuzzle shows it after 3 wrong attempts. */
  best_move?: string;
}

export interface VerifyBanner {
  text: string;
  isSuccess: boolean;
}

/** What the board should do after the server judged a move. Pure data. */
export interface VerifyDecision {
  /** Ignore the response entirely (time ended / time over). */
  ignored: boolean;
  /** New board FEN; null leaves it as is. */
  fen: string | null;
  attempts: 'reset' | 'increment' | 'keep';
  /** Server move count to remember; null leaves it as is. */
  moveCount: number | null;
  /** Give the student the board back. */
  unlock: boolean;
  banner: VerifyBanner | null;
  /** Replaces the banner shortly after (the "Your turn" nudge). */
  followUpBanner: VerifyBanner | null;
  solved: boolean;
  wrongSound: boolean;
  animateBanner: boolean;
  awardStars: boolean;
}

export interface VerifyInput {
  response: VerifyResponse | null | undefined;
  /** Board FEN the student is looking at (includes their move). */
  fen: string;
  /** Board FEN before the student's move. */
  prevFen: string;
  timeOver: boolean;
}

const TRY_AGAIN: VerifyBanner = {
  text: 'Something went wrong. Please try your move again.',
  isSuccess: false,
};

const IGNORED: VerifyDecision = {
  ignored: true,
  fen: null,
  attempts: 'keep',
  moveCount: null,
  unlock: false,
  banner: null,
  followUpBanner: null,
  solved: false,
  wrongSound: false,
  animateBanner: false,
  awardStars: false,
};

/** Put the board back and hand it to the student again. */
function rollback(prevFen: string): Partial<VerifyDecision> {
  return {fen: prevFen, attempts: 'keep', unlock: true, banner: TRY_AGAIN};
}

function opponentReply(
  fen: string,
  prevFen: string,
  response: VerifyResponse,
): Pick<VerifyDecision, 'fen' | 'unlock' | 'banner' | 'followUpBanner'> {
  let game: Chess;
  try {
    game = new Chess(fen);
  } catch {
    game = new Chess(DEFAULT_FEN);
  }
  if (game.moves().length > 0) {
    let moved = null;
    try {
      moved = game.move(response.opponent_move as string);
    } catch {
      // opponent_move from API is invalid for current position
    }
    if (moved) {
      return {
        fen: game.fen(),
        unlock: true,
        banner: {
          text: 'Correct! Please proceed with next move',
          isSuccess: true,
        },
        followUpBanner: {text: 'Your turn to move!', isSuccess: false},
      };
    }
  }
  // Reply can't be played (illegal, missing, or no legal moves): never leave
  // the board locked.
  return {followUpBanner: null, ...rollback(prevFen)} as ReturnType<
    typeof opponentReply
  >;
}

export function reduceVerifyResponse({
  response,
  fen,
  prevFen,
  timeOver,
}: VerifyInput): VerifyDecision {
  if (response?.message?.trim().toLowerCase() === 'time has ended') {
    return IGNORED;
  }
  if (timeOver) {
    return IGNORED;
  }

  const base: VerifyDecision = {...IGNORED, ignored: false};
  const awardStars = response?.valid === 1;

  if (response?.valid !== 1) {
    return {...base, fen: prevFen, attempts: 'increment', unlock: true};
  }

  if (response.incorrect === 0) {
    const moveCount = response.move_count ?? null;
    if (response.stop) {
      return {
        ...base,
        moveCount,
        attempts: 'reset',
        banner: {text: 'Solved Puzzle successfully', isSuccess: true},
        solved: true,
        animateBanner: true,
        awardStars,
      };
    }
    return {
      ...base,
      moveCount,
      attempts: 'reset',
      animateBanner: true,
      awardStars,
      ...opponentReply(fen, prevFen, response),
    };
  }

  if (response.incorrect === 1) {
    return {
      ...base,
      fen: prevFen,
      attempts: 'increment',
      unlock: true,
      wrongSound: true,
      banner: {text: 'Your move is not the best. Try again!', isSuccess: false},
      animateBanner: true,
      awardStars,
    };
  }

  // Unknown verdict: treat like a failed round trip.
  return {...base, ...rollback(prevFen), awardStars};
}

/** Decision for a verify call that never got an answer (network/server error). */
export function decisionForFailedVerify(prevFen: string): VerifyDecision {
  return {...IGNORED, ignored: false, ...rollback(prevFen)};
}
